// src/app/components/admin/WebhookMonitor.jsx
import { useState, useEffect } from "react";
import {
  getDfnsWebhookHealth,
  getDfnsWebhookLogs,
} from "../../features/admin/dfnsWebhooksApi";

export function WebhookMonitor() {
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    const fetchData = async () => {
      try {
        const [logsData, healthData] = await Promise.all([
          getDfnsWebhookLogs(10),
          getDfnsWebhookHealth(),
        ]);

        if (!alive) return;

        setRecentLogs(logsData?.logs || []);
        setStats(healthData || null);
      } catch (error) {
        console.error("Failed to fetch webhook data:", error);
        if (!alive) return;
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">Loading webhook stats...</div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "healthy":
        return "text-green-600";
      case "degraded":
        return "text-yellow-600";
      case "unhealthy":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "healthy":
        return "✅";
      case "degraded":
        return "⚠️";
      case "unhealthy":
        return "🚨";
      default:
        return "❓";
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">DFNS Webhook Monitor</h3>
        <span className={`text-2xl ${getStatusColor(stats?.status)}`}>
          {getStatusIcon(stats?.status)}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {Number.isFinite(stats?.success_rate)
              ? `${stats.success_rate.toFixed(1)}%`
              : "—"}
          </div>
          <div className="text-xs text-gray-600">Success Rate</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold">{stats?.recent_webhooks || 0}</div>
          <div className="text-xs text-gray-600">Recent Events</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {Math.floor((stats?.last_webhook_age_seconds || 0) / 60)}m
          </div>
          <div className="text-xs text-gray-600">Last Event</div>
        </div>

        <div className="text-center">
          <div className={`text-2xl font-bold ${getStatusColor(stats?.status)}`}>
            {stats?.status ? stats.status.toUpperCase() : "UNKNOWN"}
          </div>
          <div className="text-xs text-gray-600">System Status</div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold mb-2">Recent Events</h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {recentLogs.map((log) => (
            <div
              key={log.id ?? `${log.event_type}-${log.created_at}`}
              className="flex items-center justify-between text-sm border-b pb-2"
            >
              <div className="flex items-center gap-2">
                <span
                  className={
                    log.status === "SUCCESS" ? "text-green-600" : "text-red-600"
                  }
                >
                  {log.status === "SUCCESS" ? "✅" : "❌"}
                </span>
                <span className="font-mono text-xs">{log.event_type}</span>
              </div>
              <span className="text-xs text-gray-500">
                {log.created_at
                  ? new Date(log.created_at).toLocaleTimeString()
                  : "—"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
